import { createSelector } from 'reselect';
import snakeCase from 'lodash/snakeCase';
import { AppState, ModuleSlice } from '../../types/store';
import { Module, ModuleName } from '../../types/modules';
import { getCurrentGruserSlice } from './grusers';
import { Gruser } from '../../types/papi';
import { ZoneName } from '../../types/zones';
import { getModuleDataFromPapiModule } from '../helpers';

export const getAllModules = (state: AppState, gruser: Gruser | undefined): ModuleSlice =>
  getCurrentGruserSlice(state, gruser)?.modules || [];

export const getAllModuleIds = createSelector(getAllModules, modules =>
  Object.values(modules || {}).map(m => m.id)
);

export const getModule = (
  state: AppState,
  gruser: Gruser | undefined,
  moduleId: number
): Module | undefined => getAllModules(state, gruser)?.[moduleId];

export const getModuleCount = createSelector(
  getAllModules,
  (_, gruser: Gruser | undefined, zoneName: ZoneName, moduleName: ModuleName) => ({
    zoneName,
    moduleName,
  }),
  (modules, { zoneName, moduleName }) => {
    const moduleNameSnakeCase = snakeCase(moduleName);
    const zoneNameSnakeCase = snakeCase(zoneName);
    return Object.values(modules)?.filter(
      m => m.name === moduleNameSnakeCase && m.zonename === zoneNameSnakeCase
    ).length;
  }
);

export const getModuleData = createSelector(getModule, m => {
  if (!m) {
    return {};
  }
  return getModuleDataFromPapiModule(m);
});
export const getModuleMeta = createSelector(getModule, m => m?.moduleMeta);
export const getModuleName = createSelector(getModule, m => m?.name);
export const getModulePrivs = createSelector(getModule, m => m?.privileges);
export const getModuleVersion = createSelector(getModule, m => m?.version);
export const getModuleRequests = createSelector(getModule, m => m?.requests);

export const getModuleRequestsPath = (
  state: AppState,
  gruser: Gruser | undefined,
  moduleId: number,
  path: string
) => getModuleRequests(state, gruser, moduleId)?.[path];

export const getAllModulesByModuleName = createSelector(
  getAllModules,
  (_, gruser: Gruser | undefined, moduleName: ModuleName) => moduleName,
  (modules, moduleName) => {
    const moduleNameSnakeCase = snakeCase(moduleName);
    return Object.values(modules)?.filter(m => m.name === moduleNameSnakeCase);
  }
);

export const getModuleByModuleName = createSelector(getAllModulesByModuleName, modules =>
  modules.pop()
);

export const getModuleDataByModuleName = createSelector(getModuleByModuleName, module => {
  if (!module) {
    return {};
  }
  return getModuleDataFromPapiModule(module);
});

export const getModuleIdByModuleName = createSelector(getModuleByModuleName, module => module?.id);

export const canEditModule = createSelector(getModule, m => m?.privileges?.edit === true);
export const canEditModuleType = createSelector(
  getModuleByModuleName,
  m => m?.privileges?.edit === true
);
export const canDeleteModule = createSelector(getModule, m => m?.privileges?.delete === true);
